import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Mail, ArrowRight, ArrowLeft, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import SEO from '@/components/SEO';

const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const { forgotPassword, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const translations = {
    tr: {
      title: 'Şifremi Unuttum',
      subtitle: 'Şifrenizi sıfırlamak için e-posta adresinizi girin',
      email: 'E-posta',
      emailPlaceholder: 'ornek@sirket.com',
      resetPassword: 'Şifremi Sıfırla',
      sending: 'Gönderiliyor...',
      backToLogin: 'Giriş Sayfasına Dön',
      successTitle: 'E-posta Gönderildi',
      successMessage: 'E-posta adresiniz sistemde kayıtlı ise şifre sıfırlama linki gönderilecektir. Lütfen gelen kutunuzu ve spam klasörünüzü kontrol edin.',
      tryAnotherEmail: 'Başka E-posta Dene',
      errorNetwork: 'Bağlantı hatası oluştu',
    },
    en: {
      title: 'Forgot Password',
      subtitle: 'Enter your email address to reset your password',
      email: 'Email',
      emailPlaceholder: 'example@company.com',
      resetPassword: 'Reset Password',
      sending: 'Sending...',
      backToLogin: 'Back to Login',
      successTitle: 'Email Sent',
      successMessage: 'If your email address is registered in our system, a password reset link will be sent. Please check your inbox and spam folder.',
      tryAnotherEmail: 'Try Another Email',
      errorNetwork: 'Connection error occurred',
    }
  };

  const txt = translations[language] || translations.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      await forgotPassword(email);
      setIsSubmitted(true);
    } catch (err) {
      // Always show success to prevent email enumeration attacks
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden" data-testid="forgot-password-page">
      <SEO page="forgotPassword" />
      {/* Background Effects */}
      <div className="absolute inset-0 grid-bg mask-radial-fade opacity-50 pointer-events-none"></div>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="relative sharp p-3 bg-primary">
              <Shield className="h-7 w-7 text-primary-foreground" />
            </div>
            <span className="text-2xl font-bold tracking-tight">
              Threat<span className="text-primary">.</span>live
            </span>
          </Link>
        </div>

        <Card className="sharp border-border bg-card/50 backdrop-blur shadow-xl">
          <CardHeader className="text-center pb-2 border-b border-border bg-muted/30">
            <div className="soc-badge mx-auto mb-3">
              <span className="status-dot" />
              <span>// PASSWORD RECOVERY</span>
            </div>
            <CardTitle className="text-xl font-mono uppercase tracking-wider">{txt.title}</CardTitle>
            <CardDescription className="text-xs">{txt.subtitle}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {isSubmitted ? (
              /* Success State */
              <div className="text-center py-6">
                <div className="mx-auto w-14 h-14 sharp bg-primary/10 border border-primary/30 flex items-center justify-center mb-4">
                  <CheckCircle2 className="h-7 w-7 text-primary" />
                </div>
                <h3 className="text-base font-mono uppercase tracking-wider mb-2">{txt.successTitle}</h3>
                <p className="text-muted-foreground text-xs mb-6 leading-relaxed">
                  {txt.successMessage}
                </p>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    className="w-full sharp font-mono text-xs uppercase tracking-wider"
                    onClick={() => {
                      setIsSubmitted(false);
                      setEmail('');
                    }}
                  >
                    {txt.tryAnotherEmail}
                  </Button>
                  <Link to="/login" className="block">
                    <Button className="w-full sharp font-mono text-xs uppercase tracking-wider bg-primary hover:bg-primary/90">
                      <ArrowLeft className="mr-2 h-4 w-4" />
                      {txt.backToLogin}
                    </Button>
                  </Link>
                </div>
              </div>
            ) : (
              /* Form State */
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive" className="sharp bg-red-500/10 border-red-500/30">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-xs">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{txt.email}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder={txt.emailPlaceholder}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 sharp text-sm"
                      required
                      data-testid="forgot-email"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full sharp font-mono text-xs uppercase tracking-wider bg-primary hover:bg-primary/90"
                  disabled={isLoading}
                  data-testid="forgot-submit"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {txt.sending}
                    </>
                  ) : (
                    <>
                      {txt.resetPassword}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>

                <Link to="/login" className="block">
                  <Button variant="ghost" className="w-full mt-2 sharp font-mono text-xs uppercase tracking-wider">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {txt.backToLogin}
                  </Button>
                </Link>
              </form>
            )}
          </CardContent>
        </Card>

        {/* Back to Home */}
        <p className="mt-6 text-center">
          <Link to="/" className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground">
            ← {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
